import { ArrowForward } from '@mui/icons-material';
import { Box, Button, Container, Grid, Stack, Typography } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';

interface Step {
  title: string;
  description: string;
  media: { type: 'image'; src: string; alt: string } | { type: 'video'; src: string };
}

const steps: Step[] = [
  {
    title: 'Collect Geometry Data',
    description:
      'Geometry data is collected using high-precision tools such as coordinate measuring machines (CMMs) and optical 3D scanners, producing detailed point clouds or surface meshes.',
    media: { type: 'image', src: '/features/thumbnails/atos-scanbox-rotor.webp', alt: 'ATOS ScanBox optical 3D scanner' },
  },
  {
    title: 'Import into FEMORPH',
    description:
      'The measurement data is imported into FEMORPH, where it is analyzed alongside the baseline "seed" simulation model.',
    media: { type: 'image', src: '/use-cases/gui_shiny.png', alt: 'Importing scan into FEMORPH GUI' },
  },
  {
    title: 'Align and Compare',
    description:
      'FEMORPH aligns the measured geometry and baseline model, producing a detailed deviation map that quantifies local shape differences.',
    media: { type: 'image', src: '/use-cases/pbs_deviation.png', alt: 'Alignment and deviation visualization' },
  },
  {
    title: 'Morph the Mesh',
    description:
      'Robust mesh morphing transfers the measured geometric deviations to the simulation model, eliminating the need for time-consuming remeshing.',
    media: { type: 'video', src: '/turbine-morph-w-opacity-60fps-extended.mp4' },
  },
  {
    title: 'Automate with Script',
    description:
      'Every step performed in the GUI maps directly to Python function calls, which are recorded into a reusable script for future datasets.',
    media: { type: 'image', src: '/features/thumbnails/scripting.webp', alt: 'FEMORPH Python scripting interface' },
  },
];

const ProcessWorkflow: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box component="section" sx={{ bgcolor: '#f5f0e9', borderY: '1px solid', borderColor: 'divider' }}>
      <Container maxWidth="xl" sx={{ py: { xs: 6, md: 9 } }}>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ xs: 'flex-start', md: 'flex-end' }} justifyContent="space-between" sx={{ mb: 5 }}>
          <Box>
            <Typography variant="overline" color="secondary.dark">How it works</Typography>
            <Typography variant="h3" sx={{ mt: 1, maxWidth: 700 }}>
              The FEMORPH Target Surface Morphing Process
            </Typography>
          </Box>
          <Button variant="outlined" endIcon={<ArrowForward />} onClick={() => navigate('/features')}>
            Explore more features
          </Button>
        </Stack>

        <Grid container spacing={2}>
          {steps.map((step, index) => (
            <Grid item xs={12} sm={6} md={2.4} key={step.title}>

              {/* Step number + connector line */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    flexShrink: 0,
                  }}
                >
                  {index + 1}
                </Box>
                {index < steps.length - 1 && (
                  <Box sx={{ flex: 1, height: 2, bgcolor: 'divider', ml: 1, display: { xs: 'none', md: 'block' } }} />
                )}
              </Box>

              {/* Media */}
              <Box
                sx={{
                  width: '100%',
                  height: 160,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: '#eef3f1',
                  mb: 2,
                }}
              >
                {step.media.type === 'video' ? (
                  <Box
                    component="video"
                    src={step.media.src}
                    autoPlay
                    muted
                    loop
                    playsInline
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                ) : (
                  <Box
                    component="img"
                    src={step.media.src}
                    alt={step.media.alt}
                    sx={{ width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'center', p: 0.5 }}
                  />
                )}
              </Box>

              {/* Text */}
              <Typography variant="h6" sx={{ mb: 1, lineHeight: 1.3 }}>
                {step.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                {step.description}
              </Typography>

            </Grid>
          ))}
        </Grid>

      </Container>
    </Box>
  );
};

export default ProcessWorkflow;
